import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Chatbot.css';

const Chatbot: React.FC<{ logoUrl?: string; embedded?: boolean }> = ({
  logoUrl = '/logo.png',
  embedded = false
}) => {
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState<Array<{ text: string, sender: 'user' | 'bot', timestamp: string }>>([]);
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(!embedded);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const ws = useRef<WebSocket | null>(null);
  const isFirstBotMessage = useRef(true);
  const retryCount = useRef(0);
  const shouldReconnect = useRef(true);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_CHARACTERS = 100;

  const resetInactivityTimer = useCallback(() => {
    if (!embedded) return;

    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setCountdown(null);

    inactivityTimeoutRef.current = setTimeout(() => {
      let secondsLeft = 10;
      setCountdown(secondsLeft);

      countdownIntervalRef.current = setInterval(() => {
        secondsLeft--;
        setCountdown(secondsLeft);

        if (secondsLeft <= 0) {
          clearInterval(countdownIntervalRef.current!);
          setIsOpen(false);
          setCountdown(null);
        }
      }, 1000);
    }, 30000);
  }, [embedded]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setInput(value);
      setCharCount(value.length);
    }
  };

  const connectWebSocket = useCallback(() => {
    if (ws.current) {
      ws.current.onopen = null;
      ws.current.onclose = null;
      ws.current.onerror = null;
      ws.current.onmessage = null;
      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.close(1000, 'Reconnecting');
      }
      ws.current = null;
    }

    setConnectionStatus('connecting');
    ws.current = new WebSocket(`wss://rkblf1fjma.execute-api.us-east-1.amazonaws.com/prod?sessionId=${sessionId}`);

    ws.current.onopen = () => {
      setConnectionStatus('connected');
      retryCount.current = 0;
      ws.current?.send(JSON.stringify({
        action: 'connect',
        session_id: sessionId
      }));
    };

    ws.current.onclose = () => {
      if (shouldReconnect.current && retryCount.current < 5) {
        const delay = Math.min(1000 * 2 ** retryCount.current, 30000);
        retryCount.current++;
        setTimeout(connectWebSocket, delay);
      } else {
        setConnectionStatus('disconnected');
      }
    };

    ws.current.onerror = () => {
      setConnectionStatus('disconnected');
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'stream') {
          let text = data.text || '';
          if (isFirstBotMessage.current) {
            text = text.replace(/^"""\s*\.\.\.\s*"""/, '');
            isFirstBotMessage.current = false;
          }
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.sender === 'bot') {
              return [...prev.slice(0, -1), {
                ...last,
                text: last.text + text
              }];
            }
            return [...prev, {
              text,
              sender: 'bot',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }];
          });

          if (embedded && isOpen) resetInactivityTimer();
        } else if (data.type === 'end') {
          setIsStreaming(false);
          // Focus input after slight delay to ensure rendering is complete
          setTimeout(() => {
            inputRef.current?.focus();
          }, 50);
        }
      } catch {
        setIsStreaming(false);
        // Focus input even if there's an error
        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      }
    };
  }, [sessionId, embedded, isOpen, resetInactivityTimer]);

  useEffect(() => {
    const storedSession = localStorage.getItem('session_id') || crypto.randomUUID();
    localStorage.setItem('session_id', storedSession);
    setSessionId(storedSession);
    shouldReconnect.current = true;

    if (embedded && isOpen) {
      resetInactivityTimer();
    }

    return () => {
      shouldReconnect.current = false;
      if (ws.current) {
        ws.current.close(1000, 'Component unmount');
      }
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [embedded, isOpen, resetInactivityTimer]);

  useEffect(() => {
    if (sessionId) {
      connectWebSocket();
    }
  }, [sessionId, connectWebSocket]);

  useEffect(() => {
    if (embedded && isOpen) {
      resetInactivityTimer();
    }
  }, [messages, input, embedded, isOpen, resetInactivityTimer]);

  const resetChat = useCallback(() => {
    setMessages([]);
    isFirstBotMessage.current = true;
    retryCount.current = 0;

    const newSession = crypto.randomUUID();
    localStorage.setItem('session_id', newSession);
    setSessionId(newSession);
  }, []);

  const sendMessage = () => {
    const message = input.trim();
    if (!message || !ws.current || ws.current.readyState !== WebSocket.OPEN || isStreaming) return;

    const userMessage = {
      text: message,
      sender: 'user' as const,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage,
    { text: '', sender: 'bot', timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
    ]);

    ws.current.send(JSON.stringify({
      action: 'sendMessageMentorAIStream',
      session_id: sessionId,
      message
    }));

    setInput('');
    setCharCount(0);
    isFirstBotMessage.current = true;
    setIsStreaming(true);

    if (embedded) resetInactivityTimer();
  };

  if (embedded && !isOpen) {
    return (
      <button
        className="chat-launcher"
        onClick={() => {
          setIsOpen(true);
          resetInactivityTimer();
        }}
      >
        💬
      </button>
    );
  }

  return (
    <div className={`chatbot-container ${embedded ? 'embedded' : 'centered'}`}>
      <div className="chat-header">
        <img src={logoUrl} alt="Logo" className="chat-logo" />
        <span className="chat-title">Beansoft AI Assistant</span>
        <div className="connection-status-container">
          <div className={`connection-status-circle ${connectionStatus}`} title={connectionStatus}>
            {connectionStatus === 'connecting' && <span className="connecting-pulse"></span>}
          </div>
        </div>
        <button
          className="reset-btn"
          onClick={resetChat}
          disabled={connectionStatus === 'connecting'}
        >
          🔄
        </button>
      </div>

      <div className="chat-body">
        {messages.map((msg, i) => (
          <div key={i} className={`message-wrapper ${msg.sender}`}>
            <div className={`avatar ${msg.sender === 'user' ? 'user-avatar' : 'bot-avatar'}`}>{msg.sender === 'user' ? 'U' : '🤖'}</div>
            <div className={`message-bubble ${msg.sender}`}>
              <div className={`message ${msg.sender}`}>
                <div className="message-text">{msg.text}</div>
                <div className="timestamp">{msg.timestamp}</div>
              </div>
            </div>
          </div>
        ))}

        {isStreaming && (
          <div className="message-wrapper bot">
            <div className="avatar bot-avatar">🤖</div>
            <div className="message-bubble bot">
              <div className="message bot">
                <div className="message-text"><i>🤖 is typing...</i></div>
              </div>
            </div>
          </div>
        )}

        <div ref={el => el?.scrollIntoView({ behavior: 'smooth' })} />
      </div>

      <div className="chat-footer">
        {embedded && countdown !== null && (
          <div className="countdown-text">
            Minimizing in {countdown}s...
          </div>
        )}
        <div className="input-container">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              disabled={connectionStatus !== 'connected' || isStreaming}
            />
            <div className="char-counter-container">
              <span className={`char-counter ${charCount >= MAX_CHARACTERS ? 'char-counter-limit' : ''}`}>
                {charCount}/{MAX_CHARACTERS}
              </span>
            </div>
          </div>
          <button
            className="send-button"
            onClick={sendMessage}
            disabled={!input.trim() || connectionStatus !== 'connected' || isStreaming || charCount > MAX_CHARACTERS}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;